import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { noop } from 'lodash-es';
import { Box, Column } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOpenNewProposal } from '~/hooks/useOpenNewProposal';
import { useClientSummary } from '../../hooks/useClientSummary';
import { AgreementsOverviewCard } from './cards/AgreementsOverviewCard/AgreementsOverviewCard';
import { BillingOverviewCard } from './cards/BillingOverviewCard/BillingOverviewCard';
import { ClientDetailsCard } from './cards/ClientDetailsCard/ClientDetailsCard';
import { ClientReferenceFieldsSubsection } from './cards/ClientDetailsCard/ClientReferenceFieldsSubsection/ClientReferenceFieldsSubsection';
import { ContactsCard } from './cards/ContactsCard/ContactsCard';
import { ContactsCardOld } from './cards/ContactsCardOld/ContactsCardOld';
import { NeedsAttentionCard } from './cards/NeedsAttentionCard/NeedsAttentionCard';
import { useOpenAddPaymentMethodModal } from './cards/PaymentMethodsCard/AddPaymentMethodModal/hooks/useOpenAddPaymentMethodModal';
import { PaymentMethodsCard } from './cards/PaymentMethodsCard/PaymentMethodsCard';
import styles from './SummaryTab.module.scss';
export const GetSummaryTabTID = (parentTestId)=>getTestIds(parentTestId);
export function SummaryTab(param) {
    let { clientId, clientName, isArchived, isReadOnly, onManageBilling, onViewUpcomingInvoice, onBillClient, onViewAllOverdue, clientAddress, referenceFields, onEditClient, onAddReferenceField } = param;
    const { SummaryTabTestIds: TID } = useMemo(()=>GetSummaryTabTID(), []);
    const { summary } = useClientSummary(clientId);
    const { openNewProposal } = useOpenNewProposal({
        navigationState: {
            selectedClientId: clientId
        }
    });
    const isMobile = useIsMobileForHomePage();
    const openAddPaymentMethodModal = useOpenAddPaymentMethodModal(clientId);
    const { CRM_CONTACTS, CLIENT_ADDRESS, CLIENT_REFERENCE_FIELDS } = useFeatureFlags();
    const cardAddress = CLIENT_ADDRESS === true ? clientAddress : null;
    const showReferenceFields = !!CLIENT_REFERENCE_FIELDS && (referenceFields.length > 0 || !isReadOnly);
    return /*#__PURE__*/ _jsxs(Column, {
        testId: TID.Root(),
        gap: 24,
        className: styles.outer,
        children: [
            summary.needsAttention.length > 0 && /*#__PURE__*/ _jsx(Box, {
                testId: TID.NeedsAttentionSection(),
                className: styles.needsAttentionWrap,
                children: /*#__PURE__*/ _jsx(NeedsAttentionCard, {
                    items: summary.needsAttention,
                    onItemAction: noop
                })
            }),
            /*#__PURE__*/ _jsxs(Box, {
                testId: TID.ColumnsGrid(),
                className: cn(styles.root, {
                    [styles.rootMobile]: isMobile
                }),
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        testId: TID.LeftColumn(),
                        gap: 24,
                        gapMobile: 8,
                        className: styles.column,
                        children: [
                            /*#__PURE__*/ _jsx(BillingOverviewCard, {
                                customerId: clientId,
                                isReadOnly: isReadOnly,
                                onManageBilling: onManageBilling,
                                onBillClient: onBillClient,
                                onViewUpcomingInvoice: onViewUpcomingInvoice,
                                onViewAllOverdue: onViewAllOverdue
                            }),
                            /*#__PURE__*/ _jsx(AgreementsOverviewCard, {
                                customerId: clientId,
                                customerName: clientName,
                                isReadOnly: isReadOnly,
                                onCreateProposal: openNewProposal
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        testId: TID.RightColumn(),
                        gap: 24,
                        gapMobile: 8,
                        className: styles.column,
                        children: [
                            (cardAddress != null || showReferenceFields) && /*#__PURE__*/ _jsx(ClientDetailsCard, {
                                address: cardAddress,
                                referenceFieldsSubsection: showReferenceFields && /*#__PURE__*/ _jsx(ClientReferenceFieldsSubsection, {
                                    fields: referenceFields,
                                    isReadOnly: isReadOnly,
                                    onAdd: onAddReferenceField
                                }),
                                isReadOnly: isReadOnly,
                                onEditClient: onEditClient
                            }),
                            CRM_CONTACTS === true ? /*#__PURE__*/ _jsx(ContactsCard, {
                                clientId: clientId,
                                isArchived: isArchived,
                                isReadOnly: isReadOnly
                            }) : /*#__PURE__*/ _jsx(ContactsCardOld, {
                                clientId: clientId,
                                isArchived: isArchived,
                                isReadOnly: isReadOnly
                            }),
                            /*#__PURE__*/ _jsx(PaymentMethodsCard, {
                                clientId: clientId,
                                isReadOnly: isReadOnly,
                                onAddPaymentMethod: openAddPaymentMethodModal
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
